import { RootState } from 'redux/store';
import { StaffRoleEntry, StaffRoleType, effectiveRoleForParish, hasAtLeastRole, MIN_PORTAL_STAFF_ROLE } from 'api/v1Helpers';

export const selectSelectedParishId = (state: RootState): string | null => state.user.selectedParishId;

/**
 * True when the shell's selected parish moved away from `parishId` while a
 * request was in flight — callers should drop the stale response.
 */
export function parishSelectionMoved(getState: () => unknown, parishId: string): boolean {
    return selectSelectedParishId(getState() as RootState) !== parishId;
}

export const selectStaffRoles = (state: RootState): StaffRoleEntry[] => state.user.staffRoles ?? [];

export const selectEffectiveStaffRole = (state: RootState): StaffRoleType | null =>
    effectiveRoleForParish(state.user.staffRoles, state.user.selectedParishId);

/** Portal staff gate: effective role for selected parish ≥ CLERK. */
export const selectHasPortalStaffAccess = (state: RootState): boolean =>
    hasAtLeastRole(selectEffectiveStaffRole(state), MIN_PORTAL_STAFF_ROLE);

/**
 * Portal shell login gate: any staff role ≥ CLERK on any parish.
 * Membership is not required — staff rows can exist without a ParishMembership
 * (staff invite accept is still deferred on the backend). Parishioner-only
 * users (membership, no staff) remain denied.
 */
export const selectCanAccessPortal = (state: RootState): boolean => {
    const roles = selectStaffRoles(state);
    return roles.some((r) => hasAtLeastRole(r.role, MIN_PORTAL_STAFF_ROLE));
};

/** Backend shipped staffRoles on /me (even if the array is empty). */
export const selectStaffRolesContractReady = (state: RootState): boolean => state.user.staffRolesContractReady === true;

/** Provisioned staff with a temporary password — block portal until change-password. */
export const selectMustChangePassword = (state: RootState): boolean => state.user.mustChangePassword === true;

/** Boot complete for shell gates: /me + /me/parishes have settled successfully. */
export const selectPortalContextReady = (state: RootState): boolean =>
    state.user.meHydrated === true && state.user.myParishesHydrated === true;
